import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

const TABLE_OPTIONS = [
  { value: 'package_details', label: 'Package Details' },
  { value: 'purchase_orders', label: 'Purchase Orders' },
  { value: 'zoe_shipment_tracking', label: 'Zoe Shipment Tracking' },
  { value: 'zoe_orders', label: 'Zoe Orders' },
  { value: 'change_logs', label: 'Change Logs' },
  { value: 'up_users', label: 'Users' }
];

const COLUMN_OPTIONS = {
  package_details: [
    { value: 'id', label: 'ID' },
    { value: 'direction_type', label: 'Direction Type' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'recipient_name', label: 'Recipient Name' },
    { value: 'tracking_number', label: 'Tracking Number' },
    { value: 'order_number', label: 'Order Number' },
    { value: 'uploaded_by', label: 'Uploaded By' },
    { value: 'createdAt', label: 'Created At' },
    { value: 'updatedAt', label: 'Updated At' }
  ],
  purchase_orders: [
    { value: 'ID', label: 'ID' },
    { value: 'PONumber', label: 'PO Number' },
    { value: 'POTitle', label: 'PO Title' },
    { value: 'POType', label: 'PO Type' },
    { value: 'Status', label: 'Status' },
    { value: 'Requisitioner', label: 'Requisitioner' },
    { value: 'ShipTo', label: 'Ship To' },
    { value: 'ShipVia', label: 'Ship Via' },
    { value: 'Terms', label: 'Terms' },
    { value: 'MasterPO', label: 'Master PO' },
    { value: 'DateCreated', label: 'Date Created' },
    { value: 'DatePlaced', label: 'Date Placed' },
    { value: 'RequiredDate', label: 'Required Date' }
  ],
  zoe_shipment_tracking: [
    { value: 'id', label: 'ID' },
    { value: 'shipment_id', label: 'Shipment ID' },
    { value: 'tracking_number', label: 'Tracking Number' },
    { value: 'status', label: 'Status' },
    { value: 'carrier', label: 'Carrier' },
    { value: 'createdAt', label: 'Created At' },
    { value: 'updatedAt', label: 'Updated At' }
  ],
  zoe_orders: [
    { value: 'id', label: 'ID' },
    { value: 'order_number', label: 'Order Number' },
    { value: 'customer_name', label: 'Customer Name' },
    { value: 'status', label: 'Status' },
    { value: 'createdAt', label: 'Created At' },
    { value: 'updatedAt', label: 'Updated At' }
  ],
  change_logs: [
    { value: 'id', label: 'ID' },
    { value: 'table_name', label: 'Table Name' },
    { value: 'action', label: 'Action' },
    { value: 'changed_by', label: 'Changed By' },
    { value: 'change_source', label: 'Change Source' },
    { value: 'createdAt', label: 'Created At' }
  ],
  up_users: [
    { value: 'id', label: 'ID' },
    { value: 'username', label: 'Username' },
    { value: 'email', label: 'Email' },
    { value: 'usertype', label: 'User Type' },
    { value: 'is_active', label: 'Is Active' },
    { value: 'createdAt', label: 'Created At' }
  ]
};

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week-to-date', label: 'This Week to Date' },
  { value: 'this-week', label: 'This Week' },
  { value: 'last-week', label: 'Last Week' },
  { value: 'this-month-to-date', label: 'This Month to Date' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'custom', label: 'Custom' }
];

const ADVANCED_OPTIONS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' },
  { value: 'NOT', label: 'Negate' }
];

export default function ReportFilterBuilder({ onGenerateReport }) {
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [advancedOperator, setAdvancedOperator] = useState('AND');
  const [filters, setFilters] = useState([]);

  const calculateDateRange = (range) => {
    const now = new Date();
    let from, to;

    switch (range) {
      case 'today':
        from = to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        from = to = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 1
        );
        break;
      case 'this-week-to-date': {
        const dayOfWeek = now.getDay();
        from = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - dayOfWeek
        );
        to = now;
        break;
      }
      case 'this-week': {
        const startOfWeek = now.getDay();
        from = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - startOfWeek
        );
        to = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() + (6 - startOfWeek)
        );
        break;
      }
      case 'last-week': {
        const lastWeekStart = now.getDay() + 7;
        from = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - lastWeekStart
        );
        to = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - (now.getDay() + 1)
        );
        break;
      }
      case 'this-month-to-date':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = now;
        break;
      case 'this-month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last-month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      default:
        return;
    }

    setFromDate(formatDate(from));
    setToDate(formatDate(to));
  };

  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    if (dateRange && dateRange !== 'custom') {
      calculateDateRange(dateRange);
    }
  }, [dateRange]);

  const handleAddFilter = () => {
    if (!selectedColumn || !filterValue) return;

    const newFilter = {
      column: selectedColumn,
      value: filterValue,
      operator: advancedOperator,
      label:
        COLUMN_OPTIONS[selectedTable]?.find(
          (col) => col.value === selectedColumn
        )?.label || selectedColumn
    };

    setFilters([...filters, newFilter]);
    setSelectedColumn('');
    setFilterValue('');
  };

  const handleChangeFilter = (index) => {
    if (!selectedColumn || !filterValue) return;

    const updatedFilters = [...filters];
    updatedFilters[index] = {
      column: selectedColumn,
      value: filterValue,
      operator: advancedOperator,
      label:
        COLUMN_OPTIONS[selectedTable]?.find(
          (col) => col.value === selectedColumn
        )?.label || selectedColumn
    };

    setFilters(updatedFilters);
    setSelectedColumn('');
    setFilterValue('');
  };

  const handleRemoveFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleRemoveAll = () => {
    setFilters([]);
  };

  const handleOk = () => {
    if (!selectedTable) {
      alert('Please select a table');
      return;
    }

    onGenerateReport({
      table: selectedTable,
      filters,
      dateRange: {
        from: fromDate,
        to: toDate
      }
    });
  };

  const handleCancel = () => {
    setSelectedTable('');
    setSelectedColumn('');
    setFilterValue('');
    setDateRange('');
    setFromDate('');
    setToDate('');
    setFilters([]);
  };

  const availableColumns = selectedTable
    ? COLUMN_OPTIONS[selectedTable] || []
    : [];

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Report Filter Builder
      </Typography>

      <Stack spacing={3}>
        {/* Select Table */}
        <FormControl fullWidth>
          <InputLabel>Select Table</InputLabel>
          <Select
            value={selectedTable}
            label="Select Table"
            onChange={(e) => {
              setSelectedTable(e.target.value);
              setFilters([]);
              setSelectedColumn('');
            }}
          >
            {TABLE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <FormControl fullWidth>
          <InputLabel>Range</InputLabel>
          <Select
            value={dateRange}
            label="Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* From and To Dates */}
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            label="From Date (mm/dd/yyyy)"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            placeholder="mm/dd/yyyy"
            disabled={dateRange && dateRange !== 'custom'}
          />
          <TextField
            fullWidth
            label="To Date (mm/dd/yyyy)"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            placeholder="mm/dd/yyyy"
            disabled={dateRange && dateRange !== 'custom'}
          />
        </Stack>

        {/* Filter Column Selection */}
        {selectedTable && (
          <>
            <FormControl fullWidth>
              <InputLabel>Select Filter Column</InputLabel>
              <Select
                value={selectedColumn}
                label="Select Filter Column"
                onChange={(e) => setSelectedColumn(e.target.value)}
              >
                {availableColumns.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Filter Value */}
            <TextField
              fullWidth
              label="Filter Value"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder="Enter filter value"
            />

            {/* Advanced Operator */}
            <FormControl fullWidth>
              <InputLabel>Advanced (AND, OR, Negate)</InputLabel>
              <Select
                value={advancedOperator}
                label="Advanced (AND, OR, Negate)"
                onChange={(e) => setAdvancedOperator(e.target.value)}
              >
                {ADVANCED_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Add/Change Buttons */}
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleAddFilter} fullWidth>
                Add Filter
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangeFilter(filters.length - 1)}
                fullWidth
                disabled={filters.length === 0}
              >
                Change Last Filter
              </Button>
            </Stack>
          </>
        )}

        {/* Filters Selected List */}
        {filters.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Filters Selected:
            </Typography>
            <List>
              {filters.map((filter, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFilter(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={filter.operator}
                          size="small"
                          color="primary"
                        />
                        <span>
                          <strong>{filter.label}:</strong> {filter.value}
                        </span>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemoveAll}
              fullWidth
            >
              Remove All
            </Button>
          </Box>
        )}

        {/* OK and Cancel Buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOk}
            fullWidth
          >
            OK - Generate Report
          </Button>
          <Button variant="outlined" onClick={handleCancel} fullWidth>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
